import React from 'react';
import {
  Box, Typography, IconButton, MenuItem, TextField, Select, FormControl, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { FieldRow } from '../ui';
import { radius } from '../../themes/smartinventoryTheme';

// Body of the create/edit EntityDrawer — images grid and product fields.
// Split out purely to keep Products.js under the page-size budget every
// Step 5 page copies.
const ProductFormDrawer = ({
  t, form, setForm, formErrors, saving, categories, categoryById,
  displayedImages, handleFileInput, removeStagedFile,
}) => (
  <>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('Image Preview:', { defaultValue: 'Images' })}</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 1 }}>
      {displayedImages.map((src, index) => (
        <Box
          key={src + index}
          sx={{
            position: 'relative',
            height: 78,
            borderRadius: `${radius.sm}px`,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {index === 0 && (
            <Box sx={{
              position: 'absolute', top: 5, left: 5, bgcolor: 'primary.main', color: 'primary.contrastText',
              borderRadius: `${radius.pill}px`, px: 1, py: 0.25,
            }}
            >
              <Typography variant="caption" sx={{ color: 'inherit', lineHeight: 1 }}>
                {t('Primary', { defaultValue: 'Primary' })}
              </Typography>
            </Box>
          )}
          <IconButton
            size="small"
            disabled={saving}
            onClick={() => removeStagedFile(index)}
            sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box
        component="label"
        sx={{
          height: 78,
          borderRadius: `${radius.sm}px`,
          border: '1.5px dashed',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          cursor: 'pointer',
        }}
      >
        <AddPhotoAlternateIcon color="primary" fontSize="small" />
        <Typography variant="caption" color="primary">{t('Upload Images')}</Typography>
        <input hidden accept="image/*" type="file" multiple disabled={saving} onChange={handleFileInput} />
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
      {t('Multiple images, first is the primary.', { defaultValue: 'Multiple images, first is the primary.' })}
    </Typography>

    <FieldRow label={t('Product Name')} error={formErrors.productName} required>
      <TextField
        fullWidth
        value={form.productName}
        onChange={(e) => setForm({ ...form, productName: e.target.value })}
        disabled={saving}
        error={Boolean(formErrors.productName)}
      />
    </FieldRow>

    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
      <FieldRow label={t('Product Price')} error={formErrors.productPrice} required>
        <TextField
          fullWidth
          type="number"
          value={form.productPrice}
          onChange={(e) => setForm({ ...form, productPrice: e.target.value })}
          disabled={saving}
          error={Boolean(formErrors.productPrice)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
      </FieldRow>
      <FieldRow label={t('Unit')}>
        <TextField
          fullWidth
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
          disabled={saving}
        />
      </FieldRow>
    </Box>

    <FieldRow label={t('Category')} error={formErrors.categoryId} required>
      <FormControl fullWidth disabled={saving} error={Boolean(formErrors.categoryId)}>
        <Select
          value={form.categoryId}
          onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          displayEmpty
          renderValue={(value) => {
            if (!value) {
              return <Box component="span" sx={{ color: 'text.disabled' }}>{t('Select a category', { defaultValue: 'Select a category' })}</Box>;
            }
            const category = categoryById[value];
            if (!category) return t('Unknown');
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: category.categoryColor }} />
                {category.categoryName}
              </Box>
            );
          }}
        >
          {categories.map((category) => (
            <MenuItem key={category._id} value={category._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: category.categoryColor }} />
                {category.categoryName}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FieldRow>

    <FieldRow label={t('Product Description')}>
      <TextField
        fullWidth
        multiline
        rows={3}
        value={form.productDescription}
        onChange={(e) => setForm({ ...form, productDescription: e.target.value })}
        disabled={saving}
      />
    </FieldRow>

    <FieldRow
      label={`${t('Video URL (YouTube or direct)')}`}
      error={formErrors.videoUrl}
    >
      <TextField
        fullWidth
        value={form.videoUrl}
        onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
        disabled={saving}
        error={Boolean(formErrors.videoUrl)}
        placeholder="https://"
      />
    </FieldRow>
  </>
);

export default ProductFormDrawer;
